import type { GenericError, RequestStatus } from '@skynet/data-ws-core';
import type { JsonArchivedLoan } from '@skynet/loans';

export type { JsonArchivedLoan };

/** An archived project (retainer) row, as listed by `ArchivedRetainers`. */
export interface JsonArchivedRetainer {
  id: number;
  name: string;
}

export type ArchivedItem = JsonArchivedLoan | JsonArchivedRetainer;

/**
 * Wire commands of the archived loans page. This package is their only owner:
 * the domain `LoanCommands` enum no longer lists them. `ArchiveCommands` in
 * `@skynet/authz-archive` mirrors the same strings as Cerbos resource kinds, and
 * the dashboard's `archive-commands-contract.test.ts` keeps the two in step.
 */
export enum LoanArchiveCommands {
  ArchivedLoans = 'ArchivedLoans',
  SearchArchivedLoans = 'SearchArchivedLoans',
  ShowLoan = 'ShowLoan',
}

/**
 * Wire commands of the archived projects page; owned here like
 * {@link LoanArchiveCommands}.
 */
export enum RetainerArchiveCommands {
  ArchivedRetainers = 'ArchivedRetainers',
  SearchArchivedRetainers = 'SearchArchivedRetainers',
  ShowRetainer = 'ShowRetainer',
}

export interface SearchArchivedLoansPayload {
  search: string;
}

export interface ShowLoanPayload {
  loanID: number;
}

export interface SearchArchivedRetainersPayload {
  search: string;
}

export interface ShowRetainerPayload {
  projectID: number;
}

/** Outcome of a finished restore, as the page announces it to the user. */
export interface RestoreOutcome {
  /**
   * `unconfirmed` is not a failure: the socket went away between the command
   * and its ack, so the server may or may not have applied it. It is kept apart
   * from `error` so the notification does not tell the user a restore failed
   * when it may have succeeded.
   */
  status: 'success' | 'error' | 'unconfirmed';
  /**
   * Id of the item the restore was for, so a successful restore can offer to
   * open it. Taken from the request rather than the ack, which carries no id.
   */
  id: number | null;
  /**
   * Name of the item, so the notification can say which one it was after the
   * row itself is gone from the list. `null` when the row was not listed when
   * the restore went out.
   */
  name: string | null;
  /**
   * Why the restore failed or could not be confirmed, when a reason is known.
   * Always `null` on success, and on a failure whose envelope carried no
   * message.
   */
  description: string | null;
}

export interface ArchiveState {
  archivedItems: ArchivedItem[];
  /**
   * How many searches have gone out and not been answered yet.
   *
   * The `ArchivedItems` response echoes nothing of the query it answers, so it
   * can only be matched by order: one socket delivers answers in the order the
   * searches went out, which makes the last outstanding request the one the next
   * answer belongs to once the count reaches zero. Anything arriving while the
   * count is already zero answers a query that is no longer in effect — the box
   * was cleared, the search failed, or it timed out — and is dropped.
   */
  pendingSearches: number;
  /**
   * Number of the newest search sent, counting up from 0 over the slice's
   * lifetime. It lets the page's timer give up on exactly the search it was
   * started for (see `searchTimedOut`): a newer search restarts the clock.
   */
  searchSeq: number;
  requestStatus: RequestStatus | null;
  error: GenericError | null;
  /**
   * Id of the row whose restore is in flight, `null` when none is.
   *
   * The MM socket answers a restore with the shared success envelope
   * (`{ command: 'ok', okCommand: 'ShowRetainer' }`), which does not echo the
   * restored id back. This is the only record of which row the next ack refers
   * to, so exactly one restore may be in flight at a time. Three things keep
   * that true: the list disables every restore button while this is set, the
   * restore thunk's `condition` refuses to send a second command while it is
   * set, and the reducer below refuses to track one.
   */
  restoringId: number | null;
  /**
   * Name of the row whose restore is in flight, captured when the request went
   * out: by the time the ack lands the row may be gone from `archivedItems`,
   * either pruned by this slice or replaced by a filtered search result.
   */
  restoringName: string | null;
  /**
   * The finished restore this page has yet to announce, `null` once it has.
   *
   * Deliberately separate from `restoringId`: the notification announces an
   * outcome from an effect, so a restore started in between must not overwrite
   * an outcome the user has not seen. Only the page clears this, through
   * `restoreOutcomeShown`.
   *
   * It is a single slot, not a queue: a restore that *finishes* before the
   * effect has announced the previous outcome replaces it. That needs two acks
   * inside one commit, which the single-flight rule above makes unreachable —
   * and the notification is keyed per item type anyway, so a queue would only
   * have the second toast replace the first on screen instead.
   */
  restoreOutcome: RestoreOutcome | null;
}
